import React, { createContext, useContext, useEffect, useRef, useState } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('theme') as Theme | null
    return savedTheme || 'light'
  })

  const isFirstRun = useRef(true)

  useEffect(() => {
    localStorage.setItem('theme', theme)
    const firstRun = isFirstRun.current
    isFirstRun.current = false
    // Keep synthwave (restored in main.tsx) on mount; deactivate it only when the user switches themes
    if (firstRun && document.body.classList.contains('synthwave-theme')) return
    document.body.classList.remove('synthwave-theme')
    localStorage.removeItem('synthwave')
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [theme])

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))
  }

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}
